import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getEntityTypeLabel } from './incidentEntityComponents';
import { cleanDisplayName, SOURCE_TYPE_LABELS } from './citationUtils';

interface InlineCitationProps {
  citationNumber: number;
  page?: number;
  fileName: string;
  onClick: () => void;
  sourceType?: string; // 'FILES_RAG', 'WEB', 'INCIDENT', etc.
  // Additional metadata for enhanced tooltips
  incidentName?: string;
  entityType?: string;
  baseUrl?: string; // Base URL for WEB sources (e.g., "nytimes.com")
}

type TooltipContentProps = Pick<
  InlineCitationProps,
  'fileName' | 'page' | 'sourceType' | 'incidentName' | 'entityType' | 'baseUrl'
>;

const TooltipContent = ({
  fileName,
  page,
  sourceType,
  incidentName,
  entityType,
  baseUrl,
}: TooltipContentProps) => {
  const sourceLabel = sourceType
    ? SOURCE_TYPE_LABELS[sourceType] || sourceType
    : 'Source';
  const displayName = cleanDisplayName(fileName, sourceType);

  // Enhanced tooltip for INCIDENT type
  if (sourceType === 'INCIDENT') {
    const categoryLabel = entityType ? getEntityTypeLabel(entityType) : 'Data';
    return (
      <div>
        <div>
          <strong>{displayName}</strong>
        </div>
        <div className="text-muted" style={{ fontSize: '0.85em' }}>
          {sourceLabel}
          {incidentName && ` • ${incidentName}`}
        </div>
        <div className="text-muted" style={{ fontSize: '0.85em' }}>
          Category: {categoryLabel}
        </div>
      </div>
    );
  }

  // Enhanced tooltip for EXERCISE type
  if (sourceType === 'EXERCISE') {
    return (
      <div>
        <div>
          <strong>Exercise: {displayName}</strong>
        </div>
      </div>
    );
  }

  // Enhanced tooltip for WEB type - show base URL
  if (sourceType === 'WEB') {
    return (
      <div>
        <div>
          <strong>{displayName}</strong>
        </div>
        {baseUrl && (
          <div className="text-muted" style={{ fontSize: '0.85em' }}>
            Web - {baseUrl}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div>
        <strong>{displayName}</strong>
      </div>
      <div className="text-muted" style={{ fontSize: '0.85em' }}>
        {sourceLabel}
        {page && ` • Page ${page}`}
      </div>
    </div>
  );
};

export default function InlineCitation({
  citationNumber,
  fileName,
  page,
  onClick,
  sourceType,
  incidentName,
  entityType,
  baseUrl,
}: InlineCitationProps) {
  const sourceLabel = sourceType
    ? SOURCE_TYPE_LABELS[sourceType] || sourceType
    : 'source';

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`citation-${citationNumber}`}>
          <TooltipContent
            fileName={fileName}
            page={page}
            sourceType={sourceType}
            incidentName={incidentName}
            entityType={entityType}
            baseUrl={baseUrl}
          />
        </Tooltip>
      }
    >
      <button
        className="citation-link-inline"
        onClick={onClick}
        type="button"
        data-cite={citationNumber}
        aria-label={`View ${sourceLabel} citation ${citationNumber}${page ? ` on page ${page}` : ''}`}
      >
        {`[${citationNumber}]`}
      </button>
    </OverlayTrigger>
  );
}
